import { useEffect, useState } from 'react';
import {
  DevHubAlert,
  DevHubBox,
  DevHubButton,
  DevHubCircularProgress,
  DevHubGrid,
  DevHubLink,
  DevHubStep,
  DevHubStepContent,
  DevHubStepLabel,
  DevHubStepper,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { AppCreationDetails } from './AppCreationDetails';
import { AppCreationAdditionalDetails } from './AppCreationAdditionalDetails';
import { AppCreationPrivacyDetails } from './AppCreationPrivacyDetails';
import AuthenticationDetails, {
  AuthenticationData,
} from './AuthenticationDetails';
import AppCreationSubmit from './AppCreationSubmit';
import {
  AdditionalDetails,
  AppDetails,
  CorporateEntity,
  CorporateEntityProps,
  EntityProp,
  Survey,
} from './types/onboardingTypes';
import { Content, Header, Page } from '@backstage/core-components';
import { AppCreationCorporateEntity } from './AppCreationCorporateEntity';
import { ConfigHelper, Feature } from '@devxdiscover/devhub-common';

enum Step {
  ApplicationEntityDetails = 'Corporate Entity',
  ApplicationDetails = 'Application Details',
  AdditionalDetailsStep = 'Additional Details',
  PrivacyDetails = 'Privacy Details',
  AuthenticationDetailsStep = 'Authentication Details',
  Review = 'Review',
}

/**
 * Stepper component to walk user through onboarding process
 */
function AppCreation() {
  const [activeStep, setActiveStep] = useState<number>(0);
  const [validationHasError, setValidationHasError] = useState<boolean>(true);
  const [detailData, setDetailData] = useState<AppDetails>({} as AppDetails);
  const [privacyData, setPrivacyData] = useState<Survey>({} as Survey);
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
  const [toggleSubmit, setToggleSubmit] = useState<boolean>(false);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [additionalData, setAdditionalData] = useState<AdditionalDetails>(
    {} as AdditionalDetails,
  );
  const [authenticationData, setAuthenticationData] =
    useState<AuthenticationData>({} as AuthenticationData);
  const [EntityDetail, setEntityDetail] = useState<EntityProp>(
    {} as EntityProp,
  );
  const isCorpEntityEnabled = ConfigHelper.instance.isFeatureEnabled(
    Feature.CORPORATE_ENTITY,
  );
  const isPrivacySurveyEnabled = ConfigHelper.instance.isFeatureEnabled(
    Feature.PRIVACY_SURVEY,
  );

  useEffect(() => {
    if (!isCorpEntityEnabled) {
      const entityProp = CorporateEntityProps.find(
        prop => prop.name === CorporateEntity.CABLE,
      );
      if (entityProp) {
        setEntityDetail(entityProp);
        setDetailData({ ...detailData, entity: CorporateEntity.CABLE });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCorpEntityEnabled]);

  const getSteps = (flag: boolean): Step[] => {
    const steps = flag ? [Step.ApplicationEntityDetails] : [];
    if (
      (detailData.entity ?? CorporateEntity.CABLE) === CorporateEntity.CABLE
    ) {
      const additionalSteps = [
        Step.ApplicationDetails,
        Step.AdditionalDetailsStep,
        Step.AuthenticationDetailsStep,
        Step.Review,
      ];
      if (isPrivacySurveyEnabled) {
        additionalSteps.splice(2, 0, Step.PrivacyDetails);
      }
      return [...steps, ...additionalSteps];
    }
    return [
      ...steps,
      Step.ApplicationDetails,
      Step.AuthenticationDetailsStep,
      Step.Review,
    ];
  };

  const steps = getSteps(isCorpEntityEnabled);
  const handleNext = () => {
    setActiveStep(prevActiveStep => prevActiveStep + 1);
    setValidationHasError(true);
  };

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const handleReset = () => {
    setDetailData({} as AppDetails);
    setAdditionalData({} as AdditionalDetails);
    setPrivacyData({} as Survey);
    setAuthenticationData({} as AuthenticationData);
    setValidationHasError(true);
    setActiveStep(0);
    setToggleSubmit(false);
  };

  const handleReviewAndSubmit = () => {
    setToggleSubmit(true);
  };

  const handleSubmitButton = (isDisabled: boolean, isLoading: boolean) => {
    setDisableSubmit(isDisabled);
    setSubmitLoading(isLoading);
  };

  function getStepContent(step: Step) {
    switch (step) {
      case Step.ApplicationEntityDetails:
        return (
          <AppCreationCorporateEntity
            formData={detailData}
            setFormData={setDetailData}
            setEntityDetail={setEntityDetail}
            setValidationHasError={setValidationHasError}
          />
        );
      case Step.ApplicationDetails:
        return (
          <AppCreationDetails
            formData={detailData}
            setFormData={setDetailData}
            setValidationHasError={setValidationHasError}
          />
        );
      case Step.AdditionalDetailsStep:
        return (
          <AppCreationAdditionalDetails
            entity={detailData.entity}
            formData={additionalData}
            setFormData={setAdditionalData}
            setValidationHasError={setValidationHasError}
          />
        );
      case Step.PrivacyDetails:
        return (
          <AppCreationPrivacyDetails
            setValidationHasError={setValidationHasError}
            privacyData={privacyData}
            setPrivacyData={setPrivacyData}
          />
        );
      case Step.AuthenticationDetailsStep:
        return (
          <AuthenticationDetails
            authData={authenticationData}
            setAuthData={setAuthenticationData}
            setValidationHasError={setValidationHasError}
          />
        );

      case Step.Review:
        return (
          <AppCreationSubmit
            formData={detailData}
            additionalData={additionalData}
            privacyData={privacyData}
            authenticationData={authenticationData}
            EntityDetail={EntityDetail}
            handleSubmitButton={handleSubmitButton}
            toggleSubmit={toggleSubmit}
          />
        );
      default:
        return 'Unknown step';
    }
  }

  useEffect(() => {
    if (detailData.entity) {
      const entityValue = detailData.entity;
      setAdditionalData({} as AdditionalDetails);
      setPrivacyData({} as Survey);
      setAuthenticationData({} as AuthenticationData);
      setDetailData({ entity: entityValue } as AppDetails);
      setToggleSubmit(false);
    }
  }, [detailData.entity]);

  return (
    <Page themeId="home">
      <Header
        title="Application Onboarding"
        subtitle="Onboard your Application to DevHub"
      />
      <Content>
        {detailData?.entity === CorporateEntity.CABLE && (
          <DevHubBox tbPadding="medium">
            <DevHubAlert severity="error">
              Please note that creating business application entries through the
              Onboarding Wizard will trigger Security and Privacy journeys. If
              you are attempting to create an app for the GitHub Repo to
              ApplicationID mapping exercise, please refer to{' '}
              <DevHubLink
                target="_blank"
                href="https://solutions.comcast.net/p/91824/"
              >
                THIS
              </DevHubLink>{' '}
              Solutions link for possible mapping options.
            </DevHubAlert>
            <DevHubAlert severity="error">
              During onboarding, it is required to assign an IOP CI Owner Group
              and IOP Support Group. If you do not have one created you must
              create one{' '}
              <DevHubLink
                target="_blank"
                href="https://comcast.service-now.com/iop?id=sc_cat_item&sys_id=72f0ef76dbb30450b23158d6dc96196d&sysparm_category=83f98a3c1b934cd4035a32231b4bcbaf"
              >
                {' '}
                HERE
              </DevHubLink>{' '}
              prior to continuing with the onboarding process.
            </DevHubAlert>
          </DevHubBox>
        )}
        <DevHubBox>
          <DevHubStepper activeStep={activeStep} orientation="vertical">
            {steps.map(label => (
              <DevHubStep key={label}>
                <DevHubStepLabel>{label}</DevHubStepLabel>
                <DevHubStepContent>
                  {getStepContent(label)}
                  <DevHubBox>
                    <>
                      {validationHasError && (
                        <DevHubTypography paragraph variant="subtitle1">
                          Please complete form before proceeding.
                        </DevHubTypography>
                      )}
                      <DevHubGrid container direction="row">
                        <DevHubButton
                          onClick={handleReset}
                          disabled={activeStep === 0}
                        >
                          Reset
                        </DevHubButton>
                        <DevHubButton
                          disabled={activeStep === 0}
                          onClick={handleBack}
                        >
                          Back
                        </DevHubButton>
                        {activeStep !== steps.length - 1 && (
                          <DevHubButton
                            variant="contained"
                            color="primary"
                            disabled={validationHasError}
                            onClick={handleNext}
                          >
                            {activeStep === steps.length - 1
                              ? 'Finish'
                              : 'Next'}
                          </DevHubButton>
                        )}
                        {label === Step.Review &&
                          (!submitLoading ? (
                            <DevHubButton
                              onClick={handleReviewAndSubmit}
                              disabled={disableSubmit}
                              variant="contained"
                              color="primary"
                            >
                              Create Application
                            </DevHubButton>
                          ) : (
                            <DevHubCircularProgress />
                          ))}
                      </DevHubGrid>
                    </>
                  </DevHubBox>
                </DevHubStepContent>
              </DevHubStep>
            ))}
          </DevHubStepper>
        </DevHubBox>
      </Content>
    </Page>
  );
}

export default AppCreation;
